import { Card } from '@/components/common'
import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { useSEO } from '@/hooks/useSEO'

const features = [
  {
    title: 'AI-Powered Location Scoring',
    description:
      'Automatically evaluate cities and coordinates using multiple data factors to generate clear scores and location recommendations.',
    image: '/images/blis-demo/ai_scoring.png',
    useCase: 'Businesses can quickly identify high-potential and low-potential locations before investing.',
  },
  {
    title: 'Competitor & POI Insights',
    description:
      'Discover nearby competitors, footfall drivers, and key points of interest that impact store performance and customer demand',
    image: '/images/blis-demo/poi.png',
    useCase: 'Retail teams can avoid saturated areas and target underserved neighborhoods.',
  },
  {
    title: 'Interactive Maps & Reports',
    description:
      'Explore results on an interactive map and export detailed reports with scores, metrics, and visual insights.',
    image: '/images/blis-demo/interactive.png',
    useCase: 'Teams can share location analysis with stakeholders using ready-to-download reports.',
  },
]

const useCases = [
  {
    title: 'Retail Location Selection',
    description:
      'Identify the best store locations by analyzing location scores, competitor density, and nearby points of interest. Make data-driven decisions before investing in new retail spaces.',
    icon: '🏪',
  },
  {
    title: 'Franchise Expansion',
    description:
      'Evaluate potential markets and cities for franchise expansion using AI-powered location scoring. Discover underserved areas and avoid oversaturated markets.',
    icon: '📈',
  },
  {
    title: 'Competitive Analysis',
    description:
      'Understand competitor presence and market saturation in specific areas. Identify gaps in the market and opportunities for strategic positioning.',
    icon: '🔍',
  },
  {
    title: 'Site Selection & Due Diligence',
    description:
      'Evaluate multiple potential locations simultaneously with comprehensive scoring and POI insights. Export detailed reports for stakeholder review and decision-making.',
    icon: '📍',
  },
]

export const BLISDemo = () => {
  useSEO({
    title: 'BLIS Demo - Binder Location Intelligence System',
    description: 'Explore BLIS (Binder Location Intelligence System) - AI-powered location scoring, competitor insights, and interactive maps. Find the best locations for your business with data-driven analytics.',
    keywords: 'BLIS, location intelligence, location scoring, business location analytics, competitor analysis, location insights, retail location planning',
    url: 'https://bindertech.com/services/blis/demo',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'BLIS - Binder Location Intelligence System',
      applicationCategory: 'BusinessApplication',
      description: 'AI-powered location scoring and analytics platform for business location planning',
      url: 'https://bindertech.com/services/blis/demo',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'SAR',
      },
    },
  })
  return (
    <div className="relative flex min-h-screen w-full flex-col bg-background-light dark:bg-background-dark text-text-main-light dark:text-text-main-dark antialiased overflow-x-hidden transition-colors duration-300">
      <Header />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-blue-50 to-white dark:from-blue-900/20 dark:to-black/20 px-4 py-16 md:px-10 lg:px-20">
          <div className="mx-auto max-w-7xl">
            <div className="text-center mb-12 mt-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-4">
                <span className="material-symbols-outlined text-primary">analytics</span>
                <span className="text-sm font-semibold text-primary">BLIS</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-text-main-light dark:text-text-main-dark">
                Binder Location Intelligence System
              </h1>
              <p className="text-xl text-text-sub-light dark:text-text-sub-dark max-w-3xl mx-auto">
                Analyze cities and locations to find the best grocery store opportunities, with insights into nearby competitors and key points of interest.
              </p>
            </div>
          </div>
        </section>

        {/* Feature Showcases */}
        <section className="py-16 md:py-24 px-4 md:px-10 lg:px-20">
          <div className="mx-auto max-w-7xl">
            <div className="mb-12 text-center">
              <h2 className="text-3xl font-bold mb-4 text-text-main-light dark:text-text-main-dark">
                Key Features
              </h2>
              <p className="text-text-sub-light dark:text-text-sub-dark">
                Explore the powerful capabilities of BLIS and unlock smarter location insights
              </p>
            </div>

            <div className="space-y-16">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className={`flex flex-col gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                >
                  <div className="flex-1">
                    <Card className="overflow-hidden">
                      <div className="aspect-video bg-gradient-to-br from-emerald-100 to-emerald-200 dark:from-emerald-900/30 dark:to-emerald-800/30">
                        <img
                          src={feature.image}
                          alt={feature.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </Card>
                  </div>
                  <div className="flex-1 flex flex-col justify-center">
                    <h3 className="text-2xl font-semibold mb-4 text-text-main-light dark:text-text-main-dark">
                      {feature.title}
                    </h3>
                    <p className="text-text-sub-light dark:text-text-sub-dark mb-6">
                      {feature.description}
                    </p>
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <p className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">
                        Use Case Example:
                      </p>
                      <p className="text-sm text-blue-800 dark:text-blue-400">
                        {feature.useCase}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases Grid */}
        <section className="py-16 md:py-24 bg-gray-50 dark:bg-gray-900/50 px-4 md:px-10 lg:px-20">
          <div className="mx-auto max-w-7xl">
            <div className="mb-12 text-center">
              <h2 className="text-3xl font-bold mb-4 text-text-main-light dark:text-text-main-dark">
                Real-World Use Cases
              </h2>
              <p className="text-text-sub-light dark:text-text-sub-dark">
                See how organizations leverage BLIS to solve complex location intelligence challenges
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {useCases.map((useCase, index) => (
                <Card key={index} className="text-center">
                  <div className="text-4xl mb-4">{useCase.icon}</div>
                  <h3 className="text-lg font-semibold mb-2 text-text-main-light dark:text-text-main-dark">
                    {useCase.title}
                  </h3>
                  <p className="text-sm text-text-sub-light dark:text-text-sub-dark">
                    {useCase.description}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Pre-filled Data Example
        <section className="py-16 md:py-24 px-4 md:px-10 lg:px-20">
          <div className="mx-auto max-w-7xl">
            <div className="mb-12 text-center">
              <h2 className="text-3xl font-bold mb-4 text-text-main-light dark:text-text-main-dark">
                Sample Analytics Dashboard
              </h2>
              <p className="text-text-sub-light dark:text-text-sub-dark">
                Preview of the BLIS analytics interface with pre-filled data
              </p>
            </div>

            <Card className="overflow-hidden">
              <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 flex items-center justify-center">
                <div className="text-center">
                  <span className="material-symbols-outlined text-6xl text-gray-500 mb-4">
                    dashboard
                  </span>
                  <p className="text-sm text-text-sub-light dark:text-text-sub-dark">
                    /images/blis-demo/dashboard.png
                  </p>
                  <p className="text-xs text-text-sub-light dark:text-text-sub-dark mt-2">
                    Analytics dashboard screenshot placeholder
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </section> */}

        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-primary/10 to-transparent dark:from-primary/20 px-4 md:px-10 lg:px-20">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-bold mb-4 text-text-main-light dark:text-text-main-dark">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-text-sub-light dark:text-text-sub-dark mb-8">
              Discover the power of location intelligence and see what smarter insights can do.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:contact-us@binder.com
                ?subject=Service%20Subscription%20Request
                &body=Hello,%0D%0A%0D%0AI%20would%20like%20to%20submit%20a%20service%20request.%0D%0A%0D%0AORGANIZATION%20NAME:%0D%0ASERVICE%20REQUESTED:%0D%0AADDITIONAL%20DETAILS:%0D%0A%0D%0AThank%20you."
                className="flex h-12 items-center justify-center rounded-lg bg-primary px-8 text-base font-bold text-white shadow-lg shadow-primary/20 transition-all hover:bg-blue-600 hover:shadow-xl hover:shadow-primary/30 active:scale-95"
              >
                Register
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}

